/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { ApiClient } from './client';
import { ClientSetupConfig, UserAgentType, InitConfig } from './types/request';
import { ClientInterface } from './types/client';
import { initializePlatformInfo } from './helpers/platformInfo';

let storedUserAgent: UserAgentType | null = null;

function networking(setupConfig?: ClientSetupConfig | undefined) {
  const client = new ApiClient({
    baseUrl: setupConfig?.baseUrl,
    timeout: setupConfig?.timeout,
    headers: setupConfig?.headers,
    paramsSerializer: setupConfig?.paramsSerializer,
  });
  const registeredUserAgent = setupConfig?.userAgent ?? storedUserAgent;

  if (registeredUserAgent) {
    client.addUserAgent(
      registeredUserAgent.appName,
      registeredUserAgent.appVersion,
    );
  }

  if (!client) throw new Error('ApiClient returned undefined');

  return client;
}

networking.init = async (initConfig: InitConfig) => {
  storedUserAgent = initConfig?.userAgent;
  await initializePlatformInfo();
};
networking.request = <T = unknown>(
  ...args: Parameters<ClientInterface['request']>
): ReturnType<ClientInterface['request']> => networking().request<T>(...args);

networking.get = <T = unknown>(
  ...args: Parameters<ClientInterface['get']>
): ReturnType<ClientInterface['get']> => networking().get<T>(...args);

networking.post = <T = unknown>(
  ...args: Parameters<ClientInterface['post']>
): ReturnType<ClientInterface['post']> => networking().post<T>(...args);

networking.put = <T = unknown>(
  ...args: Parameters<ClientInterface['put']>
): ReturnType<ClientInterface['put']> => networking().put<T>(...args);

networking.patch = <T = unknown>(
  ...args: Parameters<ClientInterface['patch']>
): ReturnType<ClientInterface['patch']> => networking().patch<T>(...args);

networking.delete = <T = unknown>(
  ...args: Parameters<ClientInterface['delete']>
): ReturnType<ClientInterface['delete']> => networking().delete<T>(...args);

export default networking;
export { networking as CoreNetworking };
export type { ApiClient };
